import { Button, Card, Flex, Grid } from '@aws-amplify/ui-react';
import type { Edulead } from 'edulead';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import ConfirmRemoveMemberModal from '../custom-components/ConfirmRemoveMemberModal';
import LoadingBars from '../custom-components/LoadingBars';
import { RemoveButton } from '../custom-components/RemoveButton';
import { TeamMemberData, TeamMembersTable } from '../custom-components/TeamMembersTable';
import { GlobalContext, GlobalContextProps } from '../global-context';
import { allPages, enumLabel, toastV4Error } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import { default as General, default as GeneralUtils } from '../utils/GeneralUtils';
import './Team.scss';

/**
 * A member in the props of TeamMembersTable, which boss shares. The permission marks are the
 * catalog's defaults for the member's role plus the member's own grants, as Nest computed them.
 */
function toTeamMemberData(member: Edulead.EduleadV4Member, catalog: Edulead.EduleadV4Permission[]): TeamMemberData {
  const id = member.id ?? '';
  const email = member.email ?? '';
  const role = member.role;
  const byRole = catalog.filter((p) => role && p.default_roles?.includes(role)).map((p) => p.name ?? '');
  return {
    userId: id,
    username: id,
    email,
    name: [member.given_name, member.family_name].filter(Boolean).join(' ') || email,
    enabled: member.enabled ?? false,
    userCreateDate: member.create_time,
    customProfile: enumLabel(role, 'MEMBER_ROLE_'),
    permissions: [...byRole, ...(member.grants ?? [])],
  };
}

export function TeamPage(props: any) {

  const globalContext = useContext(GlobalContext);
  General.setTitle(document, 'Team', globalContext);

  const location = useLocation();

  const [team, setTeam] = useState<Edulead.EduleadV4Team>();
  const [teamMembers, setTeamMembers] = useState<TeamMemberData[]>();
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [memberToRemove, setMemberToRemove] = useState<TeamMemberData | null>(null);
  const [isRemoving, setIsRemoving] = useState<boolean>(false);

  useEffect(() => {
    if (globalContext?.authInfo?.teamId) {
      getTeam()
      getTeamMembers()
    }
  }, [globalContext.authInfo?.teamId, location.search])

  async function getTeam() {
    if (!globalContext?.authInfo?.teamId) return;
    const { team } = await eduleadV4.team.get();
    setTeam(team);
  }

  async function getTeamMembers() {
    try {
      if (!globalContext?.authInfo?.teamId) return;
      const [members, catalog] = await Promise.all([
        allPages(eduleadV4.members.list({ page_size: 500 })),
        allPages(eduleadV4.permissions.list({ page_size: 500 })),
      ]);
      setTeamMembers(members.map((m) => toTeamMemberData(m, catalog)));
    } catch (error) {
      console.error('Failed to load team members:', error);
      setTeamMembers([]);
    }
  }

  async function handleRefresh() {
    setIsRefreshing(true);
    try {
      await Promise.all([getTeam(), getTeamMembers()]);
    } finally {
      setIsRefreshing(false);
    }
  }

  function handleRemoveMemberClick(teamMember: TeamMemberData) {
    setMemberToRemove(teamMember);
  }

  async function confirmRemoveMember() {
    if (!memberToRemove) return;

    try {
      setIsRemoving(true);
      await eduleadV4.members.delete({ id: memberToRemove.userId });

      // Close modal
      setMemberToRemove(null);

      // The member is out of the team: drop the row instead of refetching
      setTeamMembers((prev) => prev?.filter((m) => m.userId !== memberToRemove.userId));

      // Show success message
      toast.success(`Successfully removed ${memberToRemove.name} from the team`);
    } catch (error) {
      toastV4Error(error, 'Failed to remove team member');
    } finally {
      setIsRemoving(false);
    }
  }

  function cancelRemoveMember() {
    setMemberToRemove(null);
  }

  return <GlobalContext.Consumer>
    {
      (globalContextProps: GlobalContextProps) => <>
        <div className="content content-1000">
          {
            team ? <>
              <h2>{ team?.name || 'Team' }</h2>
              <Card variation='outlined' className="team__card">
                <Grid
                  templateColumns="100px 1fr"
                  templateRows="1fr 1fr"
                  gap={10}
                >
                  <div>Tier</div>
                  <div className={GeneralUtils.getTier(team?.tier)?.tier === 'CUSTOMER' ? 'team__tier-customer' : ''}><span
                    data-tooltip-content='Active customer'
                    data-tooltip-hidden={GeneralUtils.getTier(team?.tier)?.tier !== 'CUSTOMER'}
                    data-tooltip-id="main-tooltip"
                  >{ GeneralUtils.getTier(team?.tier)?.humanReadable }</span></div>
                  <div>Created At</div>
                  <div>{ GeneralUtils.formatDateTime(team?.create_time) }</div>
                </Grid>
              </Card>
            </> : <LoadingBars numberOfRows={5} />
          }

          {/* Associated Domains Section */}
          {team?.email_domains && team.email_domains.length > 0 && (
            <div style={{ marginTop: 30 }}>
              <h4>Associated Domains ({ team.email_domains.length })</h4>
              <p className='desc-2'>
                New users signing up with these domains will automatically be linked to this account.
              </p>
              <Card variation='outlined' className="team__card">
                <Flex direction='column' gap={10}>
                  {team.email_domains.map((domain: string) => (
                    <div key={domain}>
                      {domain}
                    </div>
                  ))}
                </Flex>
              </Card>
            </div>
          )}

          <div className="team__members-section">
            {
              teamMembers && <>
                <Flex justifyContent='space-between' alignItems='center' style={{ marginTop: 10, marginBottom: 10 }}>
                  <h4 className="team__members-header">Team Members{ teamMembers?.length > 0 && <> ({ teamMembers?.length })</> }</h4>
                  <Button
                    className='tiny'
                    onClick={handleRefresh}
                    isDisabled={isRefreshing}
                  >Refresh</Button>
                </Flex>
              </>
            }
          </div>
        </div>
        <div className="content content-1400" style={{ paddingTop: 0 }}>
          {isRefreshing ? (
            <LoadingBars numberOfRows={3} />
          ) : teamMembers !== undefined ? (
            <TeamMembersTable
              members={teamMembers}
              currentUserEmail={globalContext?.authInfo?.userAttributes?.email}
              showPermissions={true}
              renderActions={(member) => {
                const isCurrentUser = member.email === globalContext?.authInfo?.userAttributes?.email;
                const currentUserProfile = globalContext?.authInfo?.userAttributes?.profile;
                const canRemove = currentUserProfile === 'OWNER' &&
                                 !isCurrentUser &&
                                 member.customProfile !== 'OWNER';
                if (!canRemove) return null;
                return (
                  <RemoveButton
                    title='Remove user'
                    onClick={() => handleRemoveMemberClick(member)}
                    isDisabled={isRefreshing || isRemoving}
                  />
                );
              }}
            />
          ) : (
            <LoadingBars numberOfRows={3} />
          )}
        </div>

        <ConfirmRemoveMemberModal
          isOpen={memberToRemove !== null}
          onClose={cancelRemoveMember}
          onConfirm={confirmRemoveMember}
          memberName={memberToRemove?.name || ''}
          memberEmail={memberToRemove?.email || ''}
          isLoading={isRemoving}
        />
      </>
    }
  </GlobalContext.Consumer>
}
